import ManagerRepository from 'coreClasses/ManagerRepository';
import AbstractProductModel from 'coreClasses/product/AbstractProductModel';
import CustomizedProductDecorator from 'coreClasses/product/customized/CustomizedProductDecorator';
import {ICustomizationConfiguration} from 'coreClasses/product/customized/ICustomizedProduct';

/**
 * Represents ProductListItem
 */
class ProductListItem {
	// TODO: implement type for productItem
	private managerRepository: ManagerRepository;
	private productItem: Record<string, any>;
	private productModel: AbstractProductModel;
	private customizationConfiguration: ICustomizationConfiguration;
	private customProducts: string;

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 * @param {any} productItem - the product item
	 */
	constructor(managerRepository: ManagerRepository, productItem: Record<string, any>) {
		this.managerRepository = managerRepository;
		this.productItem = productItem;
	}

	/**
	 * Returns the product list item variation group id
	 * @returns {string} - variation group id
	 */
	getVariationGroupId(): string {
		return this.productItem.c_variationGroupId;
	}

	/**
	 * Returns the product item id
	 * @returns {string} - item id
	 */
	getItemId(): string {
		return this.productItem.id;
	}

	/**
	 * Returns the product list item quantity
	 * @returns {number} - quantity
	 */
	getQuantity(): number {
		return this.productItem.quantity || 1;
	}

	/**
	 * Returns the product list item product id
	 * @returns {string} - product id
	 */
	getProductId(): string {
		return this.productItem.productId;
	}

	/**
	 * Returns the product model for product list item
	 * @returns {AbstractProductModel|CustomizedProductDecorator} product model
	 */
	getProductModel(): AbstractProductModel | typeof CustomizedProductDecorator {
		const productModel = this.managerRepository.getProductsMgr().getProduct(
			this.getVariationGroupId() || this.getProductId()
		) as AbstractProductModel;

		if (productModel?.isReady()) {
			if (this.isCustomizable()) {
				const CustomizedProductModel = CustomizedProductDecorator(productModel.constructor as any);

				this.productModel = new (CustomizedProductModel as any)(
					this.managerRepository,
					productModel.getProductObject(),
					this.getCustomizationConfiguration()
				);
			} else {
				this.productModel = productModel;
			}
		}

		return this.productModel || productModel;
	}

	/**
	 * Checks if the product list item is customizable
	 * @returns {boolean} - true if the product list item is customizable
	 */
	isCustomizable(): boolean {
		return !!this.productItem?.c_customizationJSON;
	}

	/**
	 * Returns customization id
	 * @returns {string} - customization id
	 */
	getCustomizationId(): boolean {
		return this.productItem?.c_customizationID;
	}

	/**
	 * Returns customization configuration
	 * @returns {ICustomizationConfiguration} - customization configuration
	 */
	getCustomizationConfiguration(): ICustomizationConfiguration|null {
		if (this.customizationConfiguration === undefined) {
			if (this.isCustomizable()) {
				try {
					let customImage;

					if (this.productItem?.c_customImageLink) {
						customImage = this.productItem?.c_customImageLink;
					} else if (this.productItem?.c_customizationData) {
						const customizationData = JSON.parse(this.productItem?.c_customizationData);
						customImage = customizationData.disReponse ? customizationData.disReponse[0]?.imageURL : null;
					}

					const configuration: ICustomizationConfiguration = {
						sides: [{
							customImage
						}],
						// TODO: Implement prices for customized products. https://thelevelgroup.atlassian.net/browse/DHP-616
						priceInfo: {
							totalPrice: this.productItem?.c_productPrices?.listPrices?.[0]?.value,
							currencyCode: this.productItem?.c_productPrices?.listPrices?.[0]?.currencyCode,
						} as ICustomizationConfiguration['priceInfo'],
						url: {
							queryParams: {
								customizationID: this.getCustomizationId() as any
							}
						}
					};

					this.customizationConfiguration = configuration;
				} catch {
					this.customizationConfiguration = null;
				}
			} else {
				this.customizationConfiguration = null;
			}
		}

		return this.customizationConfiguration;
	}

	/**
	 * Returns the custom products configuration
	 * @returns {string} - the custom products configuration
	 */
	getCustomProducts(){
		if (this.customProducts === undefined) {
			const customizationJSON = this.productItem?.c_customizationJSON;

			if (customizationJSON && typeof customizationJSON === 'object') {
				this.customProducts = JSON.stringify(customizationJSON);
			} else {
				this.customProducts = null;
			}

		}

		return this.customProducts;
	}
}

export default ProductListItem;